<template>
  <div class="admin-gradebook-container">
    <div class="grid-container" :style="$global.dynamicCols(5)">
      <h1>All Students</h1>
      <HiloSelect
        v-for="item in fields"
        :key="item.model"
        :options="options[item.model] || []"
        style="width: 100%; height: 40px"
        :searchable="true"
        v-model="filter[item.model]"
        :placeholder="item.placeholder"
        class="grey-theme-input"
      />
      <input
        v-model="filter.search"
        class="admin-gradebook-search"
        type="search"
        name="search"
        id="search"
        placeholder="Search..."
      />
    </div>

    <div class="admin-gradebook">
      <table class="admin-gradebook-table">
        <thead>
          <tr>
            <th></th>
            <th>Name</th>
            <th>School</th>
            <th>Grade</th>
            <th>Status</th>
            <th>Program</th>
            <th>Start</th>
            <th>End</th>
            <th>Balance</th>
            <!-- <th>$ Funded</th> -->
            <th>Profile</th>
            <th>Grade Book</th>
            <th>Log Book</th>
          </tr>
        </thead>
        <tbody v-if="!loading">
          <tr v-for="student in filteredStudents" :key="student.id">
            <td>
              <img
                v-if="student.studentImage"
                :src="student.studentImage"
                alt=""
              />
            </td>
            <td>{{ student.name }}</td>
            <td>
              {{ student.school ?? "" }}
            </td>
            <td>{{ student.grade }}%</td>

            <td>
              {{
                student.daysMargin != null
                  ? student.daysMargin +
                    (" days " + (student.daysMargin >= 0 ? "ahead" : "behind"))
                  : ""
              }}
            </td>

            <td>
              {{ student.program ?? "" }}
            </td>
            <td>
              {{ new Date(student.start).toLocaleDateString() ?? "" }}
            </td>
            <td>
              {{ new Date(student.end).toLocaleDateString() ?? "" }}
            </td>
            <td>{{ $global.formatCurrency(student.balance || 0) }}</td>
            <!-- <td>{{ $global.formatCurrency(student.funded || 0) }}</td> -->
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToProfile(student.link)"
              />
            </td>
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToGradebook(student.gradeBook)"
                v-if="student.name"
              />
            </td>
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToLogbook(student.logBook)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="loading">
        <b-spinner
          label="Loading..."
          variant="primary"
          class="spinner"
        ></b-spinner>
      </div>
      <div
        v-else-if="!filteredStudents.length"
        class="text-center mt-3 text-black-50"
      >
        No data
      </div>
    </div>

    <ToastNotifier ref="toast" />
  </div>
</template>

<script>
import { getProcessedImageUrl } from "@/util/images";
import dayjs from "dayjs";
import ToastNotifier from "../../../../../components/ToastNotifier.vue";
import LendersAPIService from "../../../../../servicehandlers/LenderAPIService.js";
import ActiveStudentsAPIService from "../../../../../servicehandlers/ActiveStudentsAPIService.js";
import HiloSelect from "../../../../../components/HiloComponents/HiloSelect.vue";
import {
  ADMIN_GRADEBOOK_TABLE_HEADERS as tableHeaders,
  PROGRAMS,
} from "../../../../../util/constants.js";

const lendersAPIService = new LendersAPIService();
const activeStudentsAPIService = new ActiveStudentsAPIService();

export default {
  components: { ToastNotifier, HiloSelect },
  props: {
    school: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      students: [],
      options: {
        grades: Array.from({ length: 10 }, (_, i) => {
          const start = i * 10;
          const end = start + 10;
          return { id: `${start}-${end}`, name: `${start}-${end}` };
        }),
        status: [
          { id: "ahead", name: "Ahead" },
          { id: "behind", name: "Behind" },
          { id: "on time", name: "On Time" },
        ],
        program: PROGRAMS,
      },
      filter: {
        grades: null,
        status: null,
        program: null,
        search: null,
      },
      fields: [
        { placeholder: "Grades", model: "grades" },
        { placeholder: "Status", model: "status" },
        { placeholder: "Program", model: "program" },
      ],
      tableHeaders,
      classes: [],
    };
  },
  computed: {
    filteredStudents() {
      let filtered = this.students.filter((student) => {
        // Filtering by grade
        const gradeMatches = this.filter.grades
          ? this.getGradeBracket(student.grade, this.filter.grades) // student.grade now
          : true;

        // Filtering by status
        const statusMatches = this.filter.status
          ? this.getStatusMatch(student.daysMargin, this.filter.status) // student.daysMargin now
          : true;

        // Filtering by program
        const programMatches = this.filter.program
          ? student.program === this.filter.program // student.program now
          : true;

        // Filtering by search query (student name)
        const searchInput = this.filter.search
          ? student.name
              .toLowerCase()
              .includes(this.filter.search.toLowerCase()) // student.name now
          : true;

        return gradeMatches && statusMatches && programMatches && searchInput;
      });

      // If a specific school filter is applied, further filter by the school
      if (this.school) {
        filtered = filtered.filter((student) => student.school === this.school);
      }

      return filtered;
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await this.getLenders();
      await this.getStudentsandSchools();
    } catch (error) {
      this.$refs.toast.showError(error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async getStudentsandSchools() {
      const lenderId = this.$store.getUser.lenderId;
      await lendersAPIService
        .getLenderGradeBook(lenderId, this.$router)
        .then(async (response) => {
          const studentSchools = response.studentsWithDetails;
          let totalGrades = 0;
          let gradeCount = 0;

          if (studentSchools && studentSchools.length > 0) {
            for (let i = 0; i < studentSchools.length; i++) {
              await activeStudentsAPIService
                .getStudentGradeDetails(studentSchools[i].Grade, this.$router)
                .then((studentResponse) => {
                  const studentGrade = studentResponse.averageGrade;
                  const studentStatus = studentResponse.daysMargin;
                  let addingToStudent = {
                    name: studentSchools[i].Name,
                    school: studentSchools[i].School,
                    daysMargin: studentStatus,
                    program: studentSchools[i].Program,
                    start: new Date(studentSchools[i].Start),
                    end: studentSchools[i].End
                      ? new Date(studentSchools[i].End)
                      : null,
                    balance: studentSchools[i].Balance,
                    funded: studentSchools[i].Funded,
                    link: studentSchools[i].Link,
                    gradeBook: studentSchools[i]["Grade Book"],
                    logBook: studentSchools[i]["Log Book"],
                    grade: studentGrade.toFixed(2),
                    studentImage: studentSchools[i].Image,
                  };
                  this.students.push(addingToStudent);

                  totalGrades += studentGrade;
                  gradeCount++;

                  if (i === studentSchools.length - 1) {
                    this.averageGrade = (totalGrades / gradeCount).toFixed(2);
                  }
                })
                .catch((error) => {});
            }
          }
        })
        .catch((error) => {});
    },
    getGradeBracket(grade, range) {
      if (!range) return true;
      if (!grade) return false;

      const [min, max] = range.split("-").map(Number);
      return grade >= min && grade < max;
    },
    getStatusMatch(daysMargin, statusFilter) {
      if (statusFilter === "ahead" && daysMargin >= 0) return true;
      if (statusFilter === "behind" && daysMargin < 0) return true;
      if (statusFilter === "on time" && daysMargin === 0) return true;
      return false;
    },
    goToProfile(id) {
      this.$router.push({
        name: "Profile Student",
        params: {
          id,
        },
      });
    },
    goToGradebook(studentId) {
      this.$router.push({
        name: "Single Student GradeBook",
        query: {
          studentId,
        },
      });
    },
    goToLogbook(userId) {
      this.$router.push({
        name: "Logbook",
        query: {
          userId,
        },
      });
    },
    async getLenders() {
      const lenderId = this.$store.getUser.lenderId;

      await lendersAPIService
        .getLendersById(lenderId, this.$router)
        .then(async (lender) => {
          this.activeLender = lender;

          await lendersAPIService
            .getLenderWithTotalAmountLent(lenderId, this.$router)
            .then((totalLent) => {
              this.activeLender.totalLent = totalLent.reduce(
                (acc, curr) => acc + curr,
                0,
              );
            });

          await lendersAPIService
            .getLenderSchoolsById(lenderId, this.$router)
            .then((schools) => {
              this.schoolList = this.$global.groupArrayByKeys(
                schools.flightSchools,
                ["id"],
                true,
              );
            });
        });
    },
  },
};
</script>
